import React, { Fragment, useCallback, useMemo } from 'react';

import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Stack } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';
import {
	ERROR_LOCATION__GLOBAL_SHORTCUTS,
	NavErrorBoundary,
	useNavBoundaryContext,
} from '@atlassian/navigation-error-boundary';

import type {
	GlobalShortcutsMenuProps,
	GlobalShortcutsProps,
	MappedGlobalShortcutsItem,
} from '../types';
import GlobalShortcutsRelayClient from '../utils/GlobalShortcutsRelayClient';
import { GlobalShortcutsWithIntlProvider } from '../utils/locale';
import { UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME } from '../utils/ufo/constants';
import { useGlobalShortcutsDataBFF } from '../utils/use-global-shortcuts-data';

import { DragAndDropOptionsProvider } from './drag-and-drop-options-provider';
import { defaultMessages } from './messages';
import { ShortcutMenuItem } from './shortcut-menu-item';
import { ViewAllAppsItem } from './view-all-apps-item';

// keep for backward compatibility
export const GlobalShortcutsWithRelayEnvironmentProvider: typeof GlobalShortcutsRelay =
	GlobalShortcutsRelay;

export const GlobalShortcuts: React.FC<GlobalShortcutsProps> = componentWithFG(
	'navx-4463-gas-agg-migration',
	GlobalShortcutsRelay,
	GlobalShortcutsBFF,
);

function GlobalShortcutsRelay(props: GlobalShortcutsProps): React.JSX.Element {
	const { product } = props;
	const shouldUseErrorMessages = fg('navx-4866-migrate-messages-from-nav-error-boundary');

	return (
		<UFOSegment name={UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME}>
			<NavErrorBoundary
				errorMessages={shouldUseErrorMessages ? defaultMessages : undefined}
				location={shouldUseErrorMessages ? undefined : ERROR_LOCATION__GLOBAL_SHORTCUTS}
				sentryConfig={{ product }}
				silent
				ufoInteractionName={UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME}
			>
				<GlobalShortcutsInnerRelay {...props} />
			</NavErrorBoundary>
		</UFOSegment>
	);
}

function GlobalShortcutsBFF(props: GlobalShortcutsProps): React.JSX.Element {
	const { product } = props;
	const shouldUseErrorMessages = fg('navx-4866-migrate-messages-from-nav-error-boundary');

	return (
		<BFFProvider consumer={BFFConsumerEnum.GLOBAL_SHORTCUTS}>
			<UFOSegment name={UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME}>
				<NavErrorBoundary
					errorMessages={shouldUseErrorMessages ? defaultMessages : undefined}
					location={shouldUseErrorMessages ? undefined : ERROR_LOCATION__GLOBAL_SHORTCUTS}
					sentryConfig={{ product }}
					silent
					ufoInteractionName={UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME}
				>
					<GlobalShortcutsInnerBFF {...props} />
				</NavErrorBoundary>
			</UFOSegment>
		</BFFProvider>
	);
}

function GlobalShortcutsInnerRelay({
	cloudId,
	product,
	shortcutItems: rawShortcutItems,
	onUpdateGetFilteredProductLinks,
	testId,
	enableDragAndDrop,
	viewAllAppsConfig,
	openInSameTab,
}: GlobalShortcutsProps) {
	const { handleError } = useNavBoundaryContext({
		location: ERROR_LOCATION__GLOBAL_SHORTCUTS,
		// Log backend errors only to Sentry
		// see: https://hello.atlassian.net/wiki/spaces/navx/pages/6439960649/NavX+-+FE+Observability#%3Acatchemall%3A--Error-handling
		sentryConfig: { product },
		disableAnalytics: true,
	});

	return (
		<GlobalShortcutsRelayClient
			cloudId={cloudId}
			onError={handleError}
			onUpdateGetFilteredProductLinks={onUpdateGetFilteredProductLinks}
			product={product}
			shortcutsConfig={rawShortcutItems}
		>
			{(shortcutItems) => (
				<GlobalShortcutsMenu
					enableDragAndDrop={enableDragAndDrop}
					openInSameTab={openInSameTab}
					product={product}
					shortcutItems={shortcutItems}
					testId={testId}
					viewAllAppsConfig={viewAllAppsConfig}
				/>
			)}
		</GlobalShortcutsRelayClient>
	);
}

function GlobalShortcutsInnerBFF({
	cloudId,
	product,
	shortcutItems: rawShortcutItems,
	onUpdateGetFilteredProductLinks,
	testId,
	enableDragAndDrop,
	viewAllAppsConfig,
	openInSameTab,
}: GlobalShortcutsProps) {
	const { handleError } = useNavBoundaryContext({
		location: ERROR_LOCATION__GLOBAL_SHORTCUTS,
		// Log backend errors only to Sentry
		// see: https://hello.atlassian.net/wiki/spaces/navx/pages/6439960649/NavX+-+FE+Observability#%3Acatchemall%3A--Error-handling
		sentryConfig: { product },
		disableAnalytics: true,
	});

	const shortcutItems = useGlobalShortcutsDataBFF({
		shortcutsConfig: rawShortcutItems,
		cloudId,
		onError: handleError,
		onUpdateGetFilteredProductLinks,
		product,
	});

	return (
		<GlobalShortcutsMenu
			enableDragAndDrop={enableDragAndDrop}
			openInSameTab={openInSameTab}
			product={product}
			shortcutItems={shortcutItems}
			testId={testId}
			viewAllAppsConfig={viewAllAppsConfig}
		/>
	);
}

// This is the GAS UI component. In apps, it is typically surrounded by one of the data components
// (above), but in the examples, we just use this directly.
export function GlobalShortcutsMenu({
	testId,
	shortcutItems = [],
	product,
	enableDragAndDrop,
	viewAllAppsConfig = {},
	openInSameTab,
	intlMode = 'async',
}: GlobalShortcutsMenuProps): React.JSX.Element {
	const IntlWrapper = intlMode === 'inherit' ? Fragment : GlobalShortcutsWithIntlProvider;
	const mappedShortcutItems: Array<MappedGlobalShortcutsItem> = useMemo(() => {
		return shortcutItems.map((item, index) => {
			let key = (item.type === 'link' && item.apsKey) || `shortcut-${index}`;
			if (item.type === 'component') {
				key = fg('navx-3656-custom-shortcut-flyout-reopen-after-dnd')
					? (item?.apsKey ?? item.menuItemId ?? `shortcut-${index}`)
					: item?.apsKey
						? item.apsKey
						: `shortcut-${index}`;
			}

			return {
				...item,
				key,
			};
		});
	}, [shortcutItems]);

	const createLinkItem = useCallback(
		(shortcut: MappedGlobalShortcutsItem) => {
			if (shortcut.type === 'component') {
				return (
					<ShortcutMenuItem
						key={shortcut.key}
						shortcut={shortcut}
						dragAndDropEnabled={enableDragAndDrop}
					/>
				);
			}

			return (
				<ShortcutMenuItem
					key={shortcut.key}
					shortcut={shortcut}
					openInSameTab={openInSameTab}
					product={product}
				/>
			);
		},
		[product, openInSameTab, enableDragAndDrop],
	);

	const shortcutList = (
		<MenuList>
			{mappedShortcutItems?.map(createLinkItem)}
			{viewAllAppsConfig?.path && <ViewAllAppsItem {...viewAllAppsConfig} product={product} />}
		</MenuList>
	);

	return (
		<IntlWrapper>
			<NavigationAnalyticsContext data={{ product }}>
				<Stack testId={testId}>
					{enableDragAndDrop ? (
						<DragAndDropOptionsProvider enabled={enableDragAndDrop}>
							{shortcutList}
						</DragAndDropOptionsProvider>
					) : (
						shortcutList
					)}
				</Stack>
			</NavigationAnalyticsContext>
		</IntlWrapper>
	);
}
